/**
 * contentWidth.js
 *
 * Enum, ordered option list and total normalizers for the per-tab dashboard content-width
 * cap (AVA-471; see .claude/features/DASHBOARD_TAB_CONTENT_WIDTH.md). A tab's
 * Configuration JSON stores the picked preset as `layout.contentWidth`; the width picker
 * renders CONTENT_WIDTH_OPTIONS in menu order, and `.custom-dashboard-grid-wrapper` binds
 * `contentWidthClass(...)` to cap and center its max-width via the `--ds-content-width-*`
 * tokens (_design-tokens.scss). Absent or corrupt stored values normalize to `full` (no
 * cap) - the default, and the exact behavior of every existing tab today.
 *
 * PURE module - no Vuex, no components, no other project imports.
 */

// The four presets. FULL is uncapped: no class, no max-width, current behavior.
export const CONTENT_WIDTH = Object.freeze({
  NARROW: 'narrow',
  MEDIUM: 'medium',
  WIDE: 'wide',
  FULL: 'full',
});

export const DEFAULT_CONTENT_WIDTH = CONTENT_WIDTH.FULL;

// Ordered for the picker menu: narrow -> medium -> wide -> full. `hint` is the caption
// shown under each label; `maxPx` mirrors the matching `--ds-content-width-*` token (null
// for full, which has no token: nothing caps it).
export const CONTENT_WIDTH_OPTIONS = Object.freeze([
  Object.freeze({ id: CONTENT_WIDTH.NARROW, label: 'Narrow', hint: '960 px', maxPx: 960 }),
  Object.freeze({ id: CONTENT_WIDTH.MEDIUM, label: 'Medium', hint: '1280 px', maxPx: 1280 }),
  Object.freeze({ id: CONTENT_WIDTH.WIDE, label: 'Wide', hint: '1600 px', maxPx: 1600 }),
  Object.freeze({ id: CONTENT_WIDTH.FULL, label: 'Full', hint: 'No limit', maxPx: null }),
]);

const VALID_CONTENT_WIDTH_IDS = new Set(Object.values(CONTENT_WIDTH));

/**
 * Normalize a raw value (stored config, picker input, anything) to a valid CONTENT_WIDTH
 * id. Anything that is not exactly one of the four known ids - wrong type, wrong case,
 * unknown string, null/undefined - falls back to DEFAULT_CONTENT_WIDTH. Total: matches
 * every input to a value, never throws.
 * @param {*} raw
 * @returns {string}
 */
export function normalizeContentWidth(raw) {
  return typeof raw === 'string' && VALID_CONTENT_WIDTH_IDS.has(raw) ? raw : DEFAULT_CONTENT_WIDTH;
}

/**
 * The utility class that caps and centers `.custom-dashboard-grid-wrapper` for a given
 * content-width id, or null for `full` (uncapped - no class, markup untouched).
 * Normalizes its input first, so junk input also yields null. Total: never throws.
 * @param {*} id
 * @returns {string|null}
 */
export function contentWidthClass(id) {
  const normalized = normalizeContentWidth(id);
  return normalized === CONTENT_WIDTH.FULL ? null : `ds-content-width--${normalized}`;
}
